import clsx from 'clsx'
import { useCallback, type ForwardedRef, type ReactNode } from 'react'
import type { View } from 'react-native'

import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import type { TBulletProps } from '../bullet'
import { badgeVariants, type TBadgeSlots, type TBadgeVariantProps } from './badgeVariants'

export type TBadgeProps = {
  /**
   * Ref to the DOM node.
   */
  ref?: ForwardedRef<View>
  /**
   * The children of the badge.
   */
  children: ReactNode
  /**
   * The content of the badge. The badge will be rendered relative to its children.
   */
  content?: TBulletProps['children']

  className?: string

  /**
   * Classname or List of classes to change the classNames of the element.
   * if `className` is passed, it will be added to the base slot.
   *
   * @example
   * ```ts
   * <Badge classNames={{
   *    base:"base-classes", // wrapper
   *    badge: "badge-classes",
   * }} />
   * ```
   */
  classNames?: SlotsToClasses<TBadgeSlots>
} & TBadgeVariantProps &
  Omit<TBulletProps, 'children'>

export function useBadge(originalProps: TBadgeProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, badgeVariants.variantKeys)

  const { children, content, classNames, className, ...otherProps } = props

  const baseStyles = clsx(classNames?.base, className)

  const slots = badgeVariants(variantProps)

  const getBulletProps = useCallback(
    () => ({
      className: slots.bubble({ class: baseStyles }),
      children: content,
      ...otherProps,
    }),
    [baseStyles, content, otherProps, slots],
  )

  const getBaseProps = useCallback(
    () => ({ className: slots.base({ class: classNames?.base }) }),
    [classNames?.base, slots],
  )

  return {
    children,
    getBaseProps,
    getBulletProps,
  }
}
